/**
 * Recent searches and recently opened items
 *
 * Stored in localStorage, scoped per team.
 * Max 8 items each.
 */

import type { EntityType } from './SearchProvider';

const MAX_RECENT_ITEMS = 8;
const STORAGE_KEY_QUERIES = 'modcar.search.recentQueries';
const STORAGE_KEY_ITEMS = 'modcar.search.recentItems';

export interface RecentItem {
  entityType: EntityType;
  id: string;
  title: string;
  subtitle?: string;
  openTarget: {
    route: string;
    dialog: string;
    idParam: string;
  };
  ts: number;
}

export interface RecentQuery {
  query: string;
  ts: number;
}

/**
 * Get storage key for team-scoped data
 */
function getStorageKey(baseKey: string, teamId: string): string {
  return `${baseKey}.${teamId}`;
}

/**
 * Safely parse JSON from localStorage
 */
function safeParseJSON<T>(value: string | null, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

/**
 * Get recent search queries for a team
 */
export function getRecentQueries(teamId: string): RecentQuery[] {
  const key = getStorageKey(STORAGE_KEY_QUERIES, teamId);
  const stored = localStorage.getItem(key);
  return safeParseJSON<RecentQuery[]>(stored, []);
}

/**
 * Add a search query to recents
 */
export function addRecentQuery(teamId: string, query: string): void {
  if (!query.trim()) return;

  const key = getStorageKey(STORAGE_KEY_QUERIES, teamId);
  const existing = getRecentQueries(teamId);

  // Remove duplicate if exists
  const filtered = existing.filter((q) => q.query.toLowerCase() !== query.toLowerCase());

  // Add new query at the beginning
  const updated: RecentQuery[] = [{ query: query.trim(), ts: Date.now() }, ...filtered].slice(
    0,
    MAX_RECENT_ITEMS
  );

  localStorage.setItem(key, JSON.stringify(updated));
}

/**
 * Remove a recent query
 */
export function removeRecentQuery(teamId: string, query: string): void {
  const key = getStorageKey(STORAGE_KEY_QUERIES, teamId);
  const existing = getRecentQueries(teamId);
  const filtered = existing.filter((q) => q.query !== query);
  localStorage.setItem(key, JSON.stringify(filtered));
}

/**
 * Clear all recent queries for a team
 */
export function clearRecentQueries(teamId: string): void {
  const key = getStorageKey(STORAGE_KEY_QUERIES, teamId);
  localStorage.removeItem(key);
}

/**
 * Get recently opened items for a team
 */
export function getRecentItems(teamId: string): RecentItem[] {
  const key = getStorageKey(STORAGE_KEY_ITEMS, teamId);
  const stored = localStorage.getItem(key);
  return safeParseJSON<RecentItem[]>(stored, []);
}

/**
 * Add an item to recently opened
 */
export function addRecentItem(teamId: string, item: Omit<RecentItem, 'ts'>): void {
  const key = getStorageKey(STORAGE_KEY_ITEMS, teamId);
  const existing = getRecentItems(teamId);

  // Remove duplicate if exists (same entityType and id)
  const filtered = existing.filter((i) => !(i.entityType === item.entityType && i.id === item.id));

  // Add new item at the beginning
  const updated: RecentItem[] = [{ ...item, ts: Date.now() }, ...filtered].slice(
    0,
    MAX_RECENT_ITEMS
  );

  localStorage.setItem(key, JSON.stringify(updated));
}

/**
 * Remove a recent item
 */
export function removeRecentItem(teamId: string, entityType: EntityType, id: string): void {
  const key = getStorageKey(STORAGE_KEY_ITEMS, teamId);
  const existing = getRecentItems(teamId);
  const filtered = existing.filter((i) => !(i.entityType === entityType && i.id === id));
  localStorage.setItem(key, JSON.stringify(filtered));
}

/**
 * Clear all recent items for a team
 */
export function clearRecentItems(teamId: string): void {
  const key = getStorageKey(STORAGE_KEY_ITEMS, teamId);
  localStorage.removeItem(key);
}

/**
 * Clear all recents for a team
 */
export function clearAllRecents(teamId: string): void {
  clearRecentQueries(teamId);
  clearRecentItems(teamId);
}
